import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export type { CountryCode };

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'FR';

const countryNames = new Intl.DisplayNames(['fr'], { type: 'region' });

export function countryToFlag(code: CountryCode): string {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function detectPhoneCountry(value: string): CountryCode | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 2) return undefined;

  const formatter = new AsYouType();
  formatter.input(trimmed.startsWith('+') ? `+${digits}` : digits);
  const country = formatter.getCountry();
  if (country) return country;

  if (trimmed.startsWith('+')) {
    return parsePhoneNumberFromString(`+${digits}`)?.country;
  }

  return (
    parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY)?.country ?? DEFAULT_PHONE_COUNTRY
  );
}

export function getPhoneFlag(value: string): string | undefined {
  const country = detectPhoneCountry(value);
  return country ? countryToFlag(country) : undefined;
}

export function formatPhoneInput(value: string): string {
  const trimmed = value.trimStart();
  if (!trimmed) return '';

  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${new AsYouType().input(digits)}` : '+';
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  return new AsYouType(DEFAULT_PHONE_COUNTRY).input(digits);
}

export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (trimmed.startsWith('+')) {
    return isValidPhoneNumber(trimmed);
  }

  return isValidPhoneNumber(trimmed, DEFAULT_PHONE_COUNTRY);
}

export function formatPhoneForSubmit(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(trimmed, DEFAULT_PHONE_COUNTRY);

  return parsed?.formatInternational() ?? trimmed;
}

export function getPhoneValidationError(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (isValidPhone(trimmed)) return undefined;

  const country = detectPhoneCountry(trimmed) ?? DEFAULT_PHONE_COUNTRY;
  const countryName = countryNames.of(country) ?? country;
  return `Le numéro n'est pas valide pour ${countryName}.`;
}
